import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  FaTachometerAlt, FaUsers, FaBoxOpen, FaMapMarkerAlt, FaSignOutAlt,
  FaUser, FaDollarSign, FaReceipt, FaFolder, FaFolderOpen, FaChevronRight, FaChevronLeft, FaClipboardList,
  FaPaypal, FaMoneyBill, FaBars, FaTimes,
  FaTags
} from 'react-icons/fa';
import { jwtDecode } from "jwt-decode";


const API_URL = '/api';

const Sidebar = ({ onLogout }) => {
  const [openSections, setOpenSections] = useState({});
  const [user, setUser] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(true); // collapsed by default
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768 && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [mobileMenuOpen]);

  useEffect(() => {
    const initUser = async () => {
      const token = localStorage.getItem('authToken');

      // 🔴 No token = real logout
      if (!token) {
        navigate('/login', { replace: true });
        return;
      }

      // ✅ STEP 1: trust token first (no API dependency)
      let decoded;
      try {
        decoded = jwtDecode(token);
        setUser({
          FullName: decoded.email.split('@')[0],
          Email: decoded.email,
          Role: decoded.role,
        });
      } catch (err) {
        console.error('Token decode failed', err);
        navigate('/login', { replace: true });
        return;
      }

      // 🔹 STEP 2: optional profile enrichment (NO logout here)
      try {
        const res = await fetch(`${API_URL}/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          if (data.user) {
            setUser(data.user);
          }
        }
      } catch (err) {
        console.warn('Profile fetch failed:', err);
      }

      // 🔹 STEP 3: optional client fetch
      try {
        const clientsRes = await fetch(`${API_URL}/clients`);
        const clientsData = await clientsRes.json();

        if (Array.isArray(clientsData) && decoded?.email) {
          const found = clientsData.find(
            c => c.email_id?.toLowerCase() === decoded.email.toLowerCase()
          );
          setClient(found || null);
        }
      } catch (err) {
        console.warn('Client fetch failed:', err);
      } finally {
        setLoading(false);
      }
    };

    initUser();
  }, [navigate]);
  const toggleSection = (label) => {
    setOpenSections(prev => ({ ...prev, [label]: !prev[label] }));
  };

  // All available sections
  const sections = [
    {
      label: 'Master Data',
      items: [
        { name: 'Dashboard', to: '/dashboard', icon: FaTachometerAlt },
        { name: 'Vendor Master', to: '/vendor-master', icon: FaUsers },
        { name: 'Product Master', to: '/product-master', icon: FaBoxOpen },
        // { name: 'Location Master', to: '/location-master', icon: FaMapMarkerAlt },
        { name: 'Client Onboarding', to: '/client-master', icon: FaUsers },
        { name: 'Users Master', to: '/user-master', icon: FaUsers },
        { name: 'Request for Quotation', to: '/rfq-items-master', icon: FaClipboardList },
        // { name: 'Delivery Terms Master', to: '/delivery-term-master', icon: FaReceipt },
        // { name: 'Payment Terms Master', to: '/payment-terms-master', icon: FaMoneyBill },
        { name: 'Api Mapping ', to: '/api-mapping-screen', icon: FaBars },
      ],
    },
    // {
    //   label: 'Finance',
    //   collapsible: true,
    //   items: [
    //     { name: 'Currency Master', to: '/currency-master', icon: FaDollarSign },
    //     { name: 'Tax Master', to: '/tax-master', icon: FaReceipt },
    //   ],
    // },
    // {
    //   label: 'Catalogs',
    //   collapsible: true,
    //   items: [
    //     { name: 'Category Master', to: '/category-master', icon: FaFolder },
    //     { name: 'Subcategory Master', to: '/subcategory-master', icon: FaFolderOpen },
    //   ],
    // },
    {
      label: 'Other',
      items: [
        { name: 'Profile', to: '/profile', icon: FaUser },
      ],
    },
  ];

  // Role-based access
  const roleBasedSections = {
    Superadmin: [
      'Dashboard', 'Vendor Master', 'Product Master', 'Location Master',
      'Client Onboarding', 'Users Master', 'Request for Quotation',
      'Currency Master', 'Tax Master', 'Category Master', 'Subcategory Master',
      'Profile', 'Delivery Terms Master', 'Payment Terms Master', 'Api Mapping ',
    ],
    Vendoradmin: [
      'Dashboard', 'Vendor Master', 'Product Master', 'Location Master', 'Request for Quotation', 'Profile', 'Delivery Terms Master', 'Payment Terms Master'
    ],
    Vendoruser: [
      'Dashboard', 'Request for Quotation', 'Profile'
    ],
  };

  // Filter menu items based on role
  const filteredSections = sections.map(section => ({
    ...section,
    items: section.items.filter(item =>
      roleBasedSections[user?.Role || 'Vendor']?.includes(item.name)
    )
  })).filter(section => section.items.length > 0);

  // Updated function to get only the first initial
  const getInitials = (name) => {
    if (!name) return '';
    return name.trim().charAt(0).toUpperCase();
  };

  const colors = {
    main: '#92C7CF',
    secondary: '#AAD7D9',
    bg: '#FBF9F1',
    light: '#E5E1DA',
    text: '#2b6777',
  };

  // Close mobile menu when navigating
  const handleNavLinkClick = () => {
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* Mobile menu button */}
      {isMobile && (
        <button
          className="fixed top-2 left-2 z-50 w-10 h-10 flex justify-center items-center 
             rounded-2xl bg-gradient-to-br from-[#92C7CF] to-[#AAD7D9] 
             text-white shadow-lg backdrop-blur-sm
             hover:scale-110 active:scale-95 
             transition-all duration-300 ease-in-out"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          title={mobileMenuOpen ? 'Close Menu' : 'Open Menu'}
        >
          {mobileMenuOpen ? (
            <FaTimes className="text-xl" />
          ) : (
            <FaBars className="text-xl" />
          )}
        </button>

      )}
      {/* Backdrop for mobile */}
      {isMobile && mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      {/* Sidebar */}
      <div
        className={`
        fixed flex flex-col justify-between z-50 transition-all duration-300
        ${isMobile
            ? `top-0 left-0 h-full w-64 p-4 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`
            : `top-2 left-3 h-[98%] rounded-3xl ${collapsed ? 'w-16 p-2' : 'w-72 p-6'}`
          }
        shadow-2xl
      `}
        style={{
          background: colors.bg,
          border: isMobile ? 'none' : `1.5px solid ${colors.secondary}`
        }}
      >
        {/* Collapse/Expand Button (Desktop only) */}
        {!isMobile && (
          <button
            className="absolute -right-4 top-6 z-50 bg-white border flex justify-center items-center border-gray-200 rounded-full shadow transition-all duration-300"
            style={{ width: 32, height: 32 }}
            onClick={() => setCollapsed((c) => !c)}
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            {collapsed ? <FaChevronRight /> : <FaChevronLeft />}
          </button>
        )}
        {/* Brand */}
        <div className={`mb-4 flex items-center justify-center ${isMobile || !collapsed ? 'py-2' : ''}`}>
          {isMobile || !collapsed ? (
            <h1 className="text-2xl text-center font-bold" style={{ color: colors.text }}>Vendor Portal</h1>
          ) : (
            <span className="text-2xl font-bold" style={{ color: colors.text }}>V</span>
          )}
        </div>
        {/* Navigation */}
        <div className={`flex-1 overflow-y-auto ${isMobile || !collapsed ? 'pr-1' : 'pr-0'} scrollbar-hidden flex flex-col items-center`}>
          {filteredSections.map((section) => (
            <div key={section.label} className={`mb-3 w-full ${isMobile || !collapsed ? '' : 'flex flex-col items-center'}`}>
              {/* Section label - show on mobile and expanded desktop */}
              {(isMobile || (!isMobile && !collapsed)) && (
                <div className="px-3 py-2 text-xs font-bold uppercase" style={{ color: colors.secondary }}>
                  {section.label}
                </div>
              )}

              {section.collapsible ? (
                <>
                  <button
                    onClick={() => toggleSection(section.label)}
                    className={`w-full text-left px-3 py-2 font-semibold rounded transition flex ${isMobile || !collapsed ? 'justify-between' : 'justify-center'} items-center`}
                    style={{ color: colors.text, background: openSections[section.label] ? colors.light : 'transparent' }}
                  >
                    <span className={`${isMobile || !collapsed ? '' : 'hidden'}`}>{section.label}</span>
                    <span className={`text-xs ${isMobile || !collapsed ? '' : 'hidden'}`}>
                      {openSections[section.label] ? '▲' : '▼'}
                    </span>
                  </button>

                  {/* Dropdown items - show on mobile when section is open */}
                  <div
                    className={`transition-all duration-300 overflow-hidden ${(isMobile && openSections[section.label]) || (!isMobile && !collapsed && openSections[section.label])
                        ? 'max-h-screen mt-2'
                        : 'max-h-0'
                      } pl-3`}
                  >
                    {section.items.map(({ name, to, icon: Icon }) => (
                      <NavLink
                        key={name}
                        to={to}
                        onClick={handleNavLinkClick}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-3 py-2 rounded text-sm font-medium transition ${isActive
                            ? 'text-white'
                            : 'text-gray-700 hover:bg-[#E5E1DA]'
                          } justify-${isMobile || !collapsed ? 'start' : 'center'}`
                        }
                        style={({ isActive }) => ({
                          background: isActive ? colors.main : 'transparent',
                          color: isActive ? '#fff' : colors.text,
                          minWidth: isMobile || !collapsed ? 160 : 0,
                        })}
                      >
                        <Icon className="text-lg" />
                        {(isMobile || !collapsed) && name}
                      </NavLink>
                    ))}
                  </div>
                </>
              ) : (
                section.items.map(({ name, to, icon: Icon }) => (
                  <NavLink
                    key={name}
                    to={to}
                    onClick={handleNavLinkClick}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded text-sm font-medium transition ${isActive
                        ? 'text-white'
                        : 'text-gray-700 hover:bg-[#E5E1DA]'
                      } justify-${isMobile || !collapsed ? 'start' : 'center'}`
                    }
                    style={({ isActive }) => ({
                      background: isActive ? colors.main : 'transparent',
                      color: isActive ? '#fff' : colors.text,
                      minWidth: isMobile || !collapsed ? 160 : 0,
                    })}
                  >
                    <Icon className="text-lg" />
                    {(isMobile || !collapsed) && name}
                  </NavLink>
                ))
              )}
            </div>
          ))}
        </div>
        {/* User Footer */}
        <div className={`border-t pt-4 ${isMobile || !collapsed ? '' : 'flex flex-col items-center'}`} style={{ borderColor: colors.secondary }}>
          <div
            className={`flex items-center gap-3 ${isMobile || !collapsed ? 'justify-start' : 'justify-center'} p-3 rounded-lg mb-3`}
            style={{ background: colors.secondary }}
          >
            {loading ? (
              <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse" />
            ) : client && client.client_id && client.logo ? (
              <img
                src={`/api/clients/${client.client_id}/logo?${Date.now()}`}
                className="w-10 h-10 rounded-full object-cover border-2 border-white shadow"
                alt="user"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : user && user.FullName ? (
              <div
                className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg shadow"
                style={{ background: colors.main, color: '#fff', border: `2px solid ${colors.bg}` }}
              >
                {getInitials(user.FullName)}
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-gray-300" />
            )}

            {(isMobile || !collapsed) && (
              <div className="text-sm">
                <p className="font-semibold" style={{ color: colors.text }}>
                  {user?.FullName || user?.Username || 'User'}
                </p>
                <p
                  className="text-xs truncate max-w-[160px]"
                  title={user?.Email}
                  style={{ color: colors.text, opacity: 0.7 }}
                >
                  {user?.Email || '-'}
                </p>
              </div>
            )}
          </div>

          {(isMobile || !collapsed) && (
            <button
              onClick={() => {
                localStorage.removeItem('authToken');
                onLogout && onLogout();
                navigate('/login', { replace: true });
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition"
              style={{ background: colors.main, color: '#fff' }}
            >
              <FaSignOutAlt /> Logout
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;